<template>
  <div
    class='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'
    :class='{ "bg-green-50 border-green-200": isVerified }'
  >
    <VerificationMethodHeader
      :method-type='methodType'
      :method-name='effectiveMethodName'
      :is-verified='isVerified'
      :challenge-status='methodResult.challengeStatus'
      :show-status='!isConfigureAction'
    />
    <VerificationMethodInputs
      v-if='!isVerified'
      :show-name-input='isConfigureAction'
      :show-code-input='showCodeInput'
      :method-name='methodNameInput'
      :code='code'
      :name-error='nameError'
      :code-error='codeError'
      :can-submit='canSubmit'
      :is-configure-action='isConfigureAction'
      :sending='sending'
      @update:method-name='updateMethodName'
      @update:code='updateCode'
      @submit='handleSubmit'
    />
    <VerificationTotpDetails
      v-if='!isVerified'
      :method-result='methodResult'
    />
  </div>
</template>

<script setup lang="ts">
  import type { VerificationActionMethodResult } from '@/api/requests/verification/verificationResponses'
  import { computed, ref, watch } from 'vue'
  import { useVerificationStore } from '@/store/verification'
  import { isVerificationMethodStatusSuccessful } from '@/utils/verification'
  import VerificationMethodHeader from '@/components/actions/CustomActions/Global/Verification/VerificationMethodHeader.vue'
  import VerificationMethodInputs from '@/components/actions/CustomActions/Global/Verification/VerificationMethodInputs.vue'
  import VerificationTotpDetails from '@/components/actions/CustomActions/Global/Verification/VerificationTotpDetails.vue'
  import { assertWebAuthnSupported,
           preparePublicKeyRequestOptions,
           publicKeyCredentialToPayload } from '@/utils/webauthn'

  const props = defineProps<{ methodResult: VerificationActionMethodResult; verificationUuid: string | null; actionType: string; methodType: string }>()

  const verificationStore = useVerificationStore()
  const code = ref('')
  const methodNameInput = ref('')
  const sending = ref(false)
  const nameError = ref<string[] | null>(null)
  const codeError = ref<string[] | null>(null)

  const isConfigureAction = computed(() => props.actionType === 'configure')
  const isPasskey = computed(() => props.methodType.toLowerCase() === 'passkey')
  const normalizedStatus = computed(() => props.methodResult.challengeStatus?.toLowerCase() ?? '')

  const showCodeInput = computed(() => {
    if (isVerified.value) {
      return false
    }

    if (isPasskey.value) {
      return false
    }

    if (isConfigureAction.value) {
      return true
    }

    if (props.verificationUuid) {
      return true
    }

    if (normalizedStatus.value === 'sent') {
      return true
    }

    return Boolean(props.methodResult.challengeToken)
  })
  const isVerified = computed(() => isVerificationMethodStatusSuccessful(props.methodResult.challengeStatus))

  const effectiveMethodName = computed(() => {
    if (isConfigureAction.value) {
      return methodNameInput.value.trim()
    }

    return props.methodResult.methodName ?? verificationStore.selectedMethodName ?? ''
  })

  const canSubmit = computed(() => {
    if (sending.value) {
      return false
    }

    if (isPasskey.value) {
      return !isConfigureAction.value || !!effectiveMethodName.value.trim()
    }

    return !!code.value.trim()
  })

  const resetErrors = () => {
    nameError.value = null
    codeError.value = null
  }

  const updateMethodName = (value: string) => {
    methodNameInput.value = value
  }

  const updateCode = (value: string) => {
    code.value = value
  }

  const handleSubmit = async () => {
    if (!canSubmit.value) {
      return
    }

    resetErrors()
    sending.value = true
    try {
      const answer = isPasskey.value
        ? await passkeyAnswer()
        : code.value.trim()

      if (isConfigureAction.value) {
        await verificationStore.addUserVerifiableMethod({
          methodName: effectiveMethodName.value,
          methodType: props.methodType,
          verificationUuid: verificationStore.getVerificationUuid(),
          answer,
        })
        await Promise.all([
          verificationStore.loadVerifiableMethods(),
          verificationStore.loadAvailableVerificationMethods(),
        ])
        if (!isConfigureAction.value) {
          methodNameInput.value = ''
        }
      } else {
        await verificationStore.answerVerificationChallenge({
          verificationUuid: verificationStore.getVerificationUuid() ?? '',
          challengeToken: props.methodResult.challengeToken ?? '',
          action: props.actionType,
          methodName: effectiveMethodName.value,
          methodType: props.methodType,
          answer,
        })
      }
      code.value = ''
    } catch (error) {
      if (isConfigureAction.value && !effectiveMethodName.value) {
        nameError.value = ['Name missing']
      }
      codeError.value = ['Incorrect code']
    } finally {
      sending.value = false
    }
  }

  const passkeyAnswer = async (): Promise<string> => {
    assertWebAuthnSupported()

    const data = props.methodResult.data ?? {
    }
    const challengeId = data.challenge_id ?? data.challengeId
    const options = data.options

    if (typeof challengeId !== 'string' || !options || typeof options !== 'object') {
      throw new Error('Passkey challenge is missing.')
    }

    const credential = await navigator.credentials.get({
      publicKey: preparePublicKeyRequestOptions(options as Parameters<typeof preparePublicKeyRequestOptions>[0]),
    })

    if (!credential) {
      throw new Error('Passkey verification was cancelled.')
    }

    return JSON.stringify({
      challenge_id: challengeId,
      passkey: publicKeyCredentialToPayload(credential),
    })
  }

  watch(() => verificationStore.selectedMethodType, (selected) => {
    if (!isConfigureAction.value) {
      return
    }
    if (selected === props.methodType) {
      methodNameInput.value = verificationStore.selectedMethodName ?? ''
    }
  }, {
    immediate: true,
  })

  watch(() => props.methodResult.methodName, (newValue) => {
    if (!isConfigureAction.value && newValue) {
      methodNameInput.value = newValue
    }
  }, {
    immediate: true,
  })

  watch(() => props.methodResult.challengeStatus, (status) => {
    if (!status) {
      return
    }

    if (status.toLowerCase() === 'failed') {
      codeError.value = ['Incorrect code']

      return
    }

    codeError.value = null
  })

</script>
